import { ArrowRight, ClipboardList, BookOpen, CreditCard, Mail } from 'lucide-react';
import Img from '../components/ui/Img';
import { LEGAL_ENTITY_NAME } from '../config/brandProfiles';

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Desktop Layout */}
      <div className="hidden lg:flex min-h-screen flex-col">
        <header className="w-full py-4 px-6" />

        <main className="flex-1 flex flex-col items-center justify-center px-4 -mt-20">
          <div className="mb-12">
            <Img
              src="/assets/bhcs-portal.webp"
              alt="BHCS Portal"
              className="w-full max-w-2xl h-auto rounded-lg"
              width={1200}
              height={800}
              priority
            />
          </div>

          {/* Portal Features + Sign In — single row */}
          <div className="flex items-center justify-center gap-10 mb-12">
            <div className="flex flex-col items-center">
              <div className="mb-3"><ClipboardList className="w-8 h-8" style={{ color: '#4285F4' }} /></div>
              <span className="text-gray-700 text-sm">Registration</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="mb-3"><BookOpen className="w-8 h-8" style={{ color: '#EA4335' }} /></div>
              <span className="text-gray-700 text-sm">Enrollment</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="mb-3"><CreditCard className="w-8 h-8" style={{ color: '#FBBC04' }} /></div>
              <span className="text-gray-700 text-sm">Payment</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="mb-3"><Mail className="w-8 h-8" style={{ color: '#34A853' }} /></div>
              <span className="text-gray-700 text-sm">Communication</span>
            </div>
            <div className="w-px h-12 bg-gray-200 self-center" />
            <a
              href="/login"
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold py-3 px-7 rounded-full shadow-md hover:shadow-lg transition-all duration-200 text-sm whitespace-nowrap"
            >
              Sign In
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </main>

        <footer className="w-full py-4 px-6 text-gray-500 text-sm bg-gray-50 border-t">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <a href="https://bhcskids.com/my-bhcs" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">About My BHCS</a>
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">Privacy Policy</a>
              <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">Terms of Service</a>
              <a href="mailto:billing@bhcskids.com" className="text-gray-600 hover:text-gray-800">Contact Us</a>
            </div>
            <div className="text-gray-500">
              &copy; {new Date().getFullYear()} {LEGAL_ENTITY_NAME}. All rights reserved.
            </div>
          </div>
        </footer>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col min-h-screen">
        <header className="w-full py-3 px-4 flex-shrink-0" />

        <main className="flex-1 flex flex-col items-center justify-center px-4 py-6 min-h-0">
          <div className="w-full max-w-sm flex flex-col items-center">
            <div className="mb-6 w-full">
              <Img
                src="/assets/bhcs-portal.webp"
                alt="BHCS Portal"
                className="w-full h-auto rounded-lg"
                width={1200}
                height={800}
                priority
              />
            </div>

            {/* Portal Features */}
            <div className="flex items-center justify-center gap-4 mb-5 w-full">
              <div className="text-center flex-1">
                <div className="mb-1 flex justify-center"><ClipboardList className="w-5 h-5" style={{ color: '#4285F4' }} /></div>
                <span className="text-gray-700 text-xs block">Registration</span>
              </div>
              <div className="text-center flex-1">
                <div className="mb-1 flex justify-center"><BookOpen className="w-5 h-5" style={{ color: '#EA4335' }} /></div>
                <span className="text-gray-700 text-xs block">Enrollment</span>
              </div>
              <div className="text-center flex-1">
                <div className="mb-1 flex justify-center"><CreditCard className="w-5 h-5" style={{ color: '#FBBC04' }} /></div>
                <span className="text-gray-700 text-xs block">Payment</span>
              </div>
              <div className="text-center flex-1">
                <div className="mb-1 flex justify-center"><Mail className="w-5 h-5" style={{ color: '#34A853' }} /></div>
                <span className="text-gray-700 text-xs block">Communication</span>
              </div>
            </div>
            {/* Sign In */}
            <a
              href="/login"
              className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold py-3 px-8 rounded-full shadow-md transition-all duration-200 text-sm w-full mb-6"
            >
              Sign In
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </main>

        <footer className="w-full py-3 px-4 text-gray-500 text-xs bg-gray-50 border-t flex-shrink-0">
          <div className="flex flex-col space-y-2">
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://bhcskids.com/my-bhcs" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">About My BHCS</a>
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">Privacy Policy</a>
              <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-800">Terms of Service</a>
              <a href="mailto:billing@bhcskids.com" className="text-gray-600 hover:text-gray-800">Contact Us</a>
            </div>
            <div className="text-center text-gray-500">
              &copy; {new Date().getFullYear()} {LEGAL_ENTITY_NAME}. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
